/**
 * Sign-in through the IDC Platform SDK shell (portal-hosted /sdk/v1/shell.js, loaded by index.html before this
 * bundle). The shell runs MSAL against the shared IDC portal Entra app on /auth/login and /auth/callback, and exposes
 * window.idcShell.getToken() for API calls. This module only waits for it, asks it for tokens, and sends the browser
 * to /auth/login when there is no session. Tokens are never stored or logged here.
 */
// Dependency-free storage helpers only, so auth never loads the Workbench screen.
import { clearPortalStorage } from "../screens/workbench/storage.ts";

export interface IdcShell {
  getToken: () => Promise<string | null>;
  signOut: () => void;
}

declare global {
  interface Window {
    idcShell?: IdcShell;
    /** Set by public/shell-load-status.js when the shell script fails to load (AIC-344). */
    idcShellLoadFailed?: boolean;
    /** Set by public/shell-load-status.js when the shell script loaded. */
    idcShellLoaded?: boolean;
  }
}

export interface Auth {
  /** An access token for the tracker-api audience, or null when there is no session. */
  getToken(): Promise<string | null>;
  /** Full-page redirect to the SDK login bootstrap, returning here afterwards. */
  signIn(): void;
  signOut(): void;
  /** Safe destination retained by the SDK across its callback. */
  returnTo?(): string;
  /** True when a sign-in redirect was already attempted in the last minute (stops a 401 → login → 401 loop). */
  recentlyTriedSignIn(): boolean;
}

export class SignInError extends Error {
  constructor(readonly reason: "failed" | "unavailable") {
    super(reason === "unavailable" ? "IDC portal sign-in is unavailable" : "IDC sign-in failed");
    this.name = "SignInError";
  }
}

export function callbackFailed(path: string, query: URLSearchParams, hash = ""): boolean {
  return isAuthRoute(path) && Boolean(query.get("error") || new URLSearchParams(hash.replace(/^#/, "")).get("error"));
}

/** Keep only a local destination; never return to the SDK bootstrap itself. */
export function signInReturnTo(location: Pick<Location, "pathname" | "search">, fallback = "/"): string {
  const raw = isAuthRoute(location.pathname) ? new URLSearchParams(location.search).get("returnTo") ?? fallback : location.pathname + location.search;
  const safe = (value: string) => value.startsWith("/") && !value.startsWith("//") && !/[\\\x00-\x20\x7f-\x9f]/.test(value);
  try {
    if (!safe(raw) || !safe(decodeURIComponent(raw))) return "/";
    const url = new URL(raw, "https://localhost");
    const normalized = url.pathname + url.search + url.hash;
    if (url.origin !== "https://localhost" || !safe(normalized) || !safe(decodeURIComponent(normalized))) return "/";
    return isAuthRoute(url.pathname) ? "/" : normalized;
  } catch { return "/"; }
}

const TRIED_KEY = "swtracker.signin_at";

/** /auth/login and /auth/callback belong to the shell: the app renders a placeholder and lets it redirect. */
export const isAuthRoute = (path: string) => path === "/auth/login" || path === "/auth/callback";

function storage(win: Window): Storage | null {
  try {
    return win.sessionStorage;
  } catch {
    return null;
  }
}

const CLEARS_FIRST = Symbol.for("swtracker.signOut.clearsWorkbench");
type WrappedSignOut = (() => void) & { [CLEARS_FIRST]?: true };

/**
 * Every sign-out clears this browser's Analyst Workbench keys first (AIC-404), including the SDK's own account-panel
 * Sign out, which looks up window.idcShell.signOut when it is clicked (idc-portal sdk/shell chrome.ts and
 * shell.entry.ts; the deployed /sdk/v1/shell.js does the same). Wrapped once: a second call leaves it as it is.
 */
export function clearWorkbenchOnSignOut(shell: IdcShell): void {
  if ((shell.signOut as WrappedSignOut)[CLEARS_FIRST]) return;
  const original = shell.signOut;
  const wrapped: WrappedSignOut = () => {
    clearPortalStorage();
    original.call(shell);
  };
  wrapped[CLEARS_FIRST] = true;
  shell.signOut = wrapped;
}

/** How long the app waits for the IDC portal's shell before saying sign-in is unavailable. */
export const SHELL_WAIT_MS = 10_000;

export function shellAuth(win: Window = window, waitMs = SHELL_WAIT_MS): Auth {
  const shell = (): Promise<IdcShell | null> => new Promise((resolve) => {
    const started = Date.now();
    const tick = () => {
      if (win.idcShell) { clearWorkbenchOnSignOut(win.idcShell); return resolve(win.idcShell); }
      // A shell script that failed to load will never define idcShell: report it now rather than after waitMs.
      if (win.idcShellLoadFailed || Date.now() - started >= waitMs) return resolve(null);
      win.setTimeout(tick, 50);
    };
    tick();
  });
  const returnTo = () => signInReturnTo(win.location, storage(win)?.getItem("idc_returnTo") ?? "/");
  return {
    returnTo,
    async getToken() {
      const s = await shell();
      if (!s) throw new SignInError("unavailable");
      try { return await s.getToken(); } catch { throw new SignInError("failed"); }
    },
    signIn() {
      storage(win)?.setItem(TRIED_KEY, String(Date.now()));
      const here = returnTo();
      win.location.assign(`/auth/login?returnTo=${encodeURIComponent(here)}`);
    },
    signOut() {
      if (!win.idcShell) throw new SignInError("failed");
      clearWorkbenchOnSignOut(win.idcShell); win.idcShell.signOut();
    },
    recentlyTriedSignIn() {
      const at = Number(storage(win)?.getItem(TRIED_KEY) ?? 0);
      return Date.now() - at < 60_000;
    },
  };
}
